import { ReactNode, forwardRef, ComponentPropsWithoutRef } from "react";
import { LucideIcon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * SectionCard — the canonical card-with-header pattern used across detail
 * pages (Application, Configuration, etc.).
 *
 * Enforces the agreed typography (`text-base` CardTitle), spacing
 * (default CardHeader/CardContent), and icon-in-title layout so individual
 * pages can't drift from the design system. If you find yourself reaching
 * for a custom CardHeader className just to tweak spacing or add an action,
 * extend this component instead — don't fork it inline.
 */
export interface SectionCardProps
  extends Omit<ComponentPropsWithoutRef<typeof Card>, "title"> {
  /** Section title (required). Rendered with `text-base` typography. */
  title: ReactNode;
  /** Optional icon shown inline before the title. */
  icon?: LucideIcon;
  /** Optional description rendered under the title. */
  description?: ReactNode;
  /** Optional element rendered to the right of the title (e.g. a button). */
  headerAction?: ReactNode;
  /** Visual variant — `destructive` adds a red border for warning cards. */
  variant?: "default" | "destructive";
  /** Additional class names for the inner CardContent. */
  contentClassName?: string;
  /** Card body content. */
  children: ReactNode;
}

export const SectionCard = forwardRef<HTMLDivElement, SectionCardProps>(
  (
    {
      title,
      icon: Icon,
      description,
      headerAction,
      variant = "default",
      className,
      contentClassName,
      children,
      ...rest
    },
    ref
  ) => {
    const hasAction = Boolean(headerAction);
    return (
      <Card
        ref={ref}
        className={cn(
          variant === "destructive" && "border-destructive/40",
          className
        )}
        {...rest}
      >
        <CardHeader
          className={
            hasAction
              ? "flex flex-row items-center justify-between space-y-0 pb-2"
              : undefined
          }
        >
          <div className={hasAction ? "space-y-1.5" : undefined}>
            <CardTitle
              className={cn(
                "text-base flex items-center gap-2",
                variant === "destructive" && "text-destructive"
              )}
            >
              {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              {title}
            </CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          {headerAction}
        </CardHeader>
        <CardContent className={contentClassName}>{children}</CardContent>
      </Card>
    );
  }
);
SectionCard.displayName = "SectionCard";
